import React from "react";
import { Box } from "@mui/material";

// ──────────────────────────────────────────────────────────────────────────────
// Column provenance + human explanation
// ──────────────────────────────────────────────────────────────────────────────
// Kept in a separate module (keyed by ColumnDef.id) so the 60+ entries in
// ALL_COLUMNS stay focused on rendering and this stays focused on documenting.

/**
 * Where a column's value comes from:
 * - "api"   → raw metric downloaded from the platform API (DV360, Meta…).
 * - "brief" → planning data typed by the team in the briefing / briefing goal.
 * - "calc"  → computed by the WIP from the two above.
 */
export type ColumnSource = "api" | "brief" | "calc";

/**
 * What a calculated column feeds on. Knowing this is what makes a wrong number
 * diagnosable: "api" → look at the platform connection or the Source ID,
 * "brief" → look at the plan, "mixed" → could be either.
 */
export type CalcInput = "api" | "brief" | "mixed";

export interface ColumnMeta {
  source: ColumnSource;
  /** Only meaningful when source is "calc". */
  inputs?: CalcInput;
  /** One-liner shown in the header tooltip and the column info dialog. */
  description: string;
}

export const SOURCE_META: Record<
  ColumnSource,
  { short: string; label: string; color: string; help: string }
> = {
  api: {
    short: "API",
    label: "API de plataforma",
    color: "#0f766e",
    help: "Dato bruto que baja de la API de la plataforma (DV360, Meta…). No se edita a mano: si algo no cuadra, se revisa la conexión o el Source ID.",
  },
  brief: {
    short: "BRIEF",
    label: "Brief",
    color: "#475569",
    help: "Dato de planificación: lo introduce el equipo en el briefing o en el objetivo de la subcampaña.",
  },
  calc: {
    short: "CALC",
    label: "Calculado",
    color: "#94a3b8",
    help: "Lo calcula el WIP. Cada columna indica de qué se alimenta: solo API, solo brief, o los dos.",
  },
};

export const CALC_INPUT_META: Record<
  CalcInput,
  { label: string; help: string }
> = {
  api: {
    label: "a partir de datos de API",
    help: "Solo usa números que reporta la plataforma. Si sale raro, el problema está en la conexión o en el Source ID, no en el brief.",
  },
  brief: {
    label: "a partir de datos de brief",
    help: "Solo usa datos del plan (fechas, volumen previsto, costes unitarios). Si sale raro, se revisa el briefing.",
  },
  mixed: {
    label: "a partir de brief + API",
    help: "Cruza plan y entrega real, así que un valor raro puede venir de cualquiera de los dos lados.",
  },
};

export const COLUMN_META: Record<string, ColumnMeta> = {
  // ── Identificación ──────────────────────────────────────────────────────────
  companyName: {
    source: "brief",
    description: "Cliente propietario del briefing.",
  },
  associatedCampaignName: {
    source: "brief",
    description:
      "Subcampaña asociada: la línea de compra concreta dentro del briefing. Es la unidad que mide este WIP (una fila = una subcampaña).",
  },
  platform: {
    source: "brief",
    description: "Plataforma donde se compra (DV360, Meta, etc.).",
  },
  briefingCampaignType: {
    source: "brief",
    description:
      "Tipo de compra declarado en el briefing para esa plataforma (display, vídeo, social…).",
  },
  briefingName: {
    source: "brief",
    description: "Briefing del que cuelga la subcampaña.",
  },
  sourceId: {
    source: "brief",
    description:
      "ID de la campaña en la plataforma. Se introduce a mano y es la clave con la que el WIP cruza las métricas de la API: si está mal o vacío, la fila no traerá datos.",
  },
  investmentPayment: {
    source: "brief",
    description:
      "Quién paga la inversión de plataforma: el cliente directamente o Ti Media Group SL.",
  },

  // ── Brief ───────────────────────────────────────────────────────────────────
  startDate: {
    source: "brief",
    description: "Fecha de inicio planificada de la subcampaña.",
  },
  endDate: {
    source: "brief",
    description: "Fecha de fin planificada de la subcampaña.",
  },
  objective: {
    source: "brief",
    description:
      "Objetivo de compra. Define qué métrica cuenta como «volumen»: CPM → impresiones, CPC → clicks, CPV/CPMV → visualizaciones completas, CPA/CPL → conversiones.",
  },
  grossUnitCost: {
    source: "brief",
    description:
      "Coste unitario que se factura al cliente (bruto, con margen incluido).",
  },
  netUnitCost: {
    source: "brief",
    description:
      "Coste unitario objetivo en plataforma (neto, sin margen). Es la referencia contra la que se compara el coste unitario real.",
  },
  totalInversion: {
    source: "brief",
    description: "Inversión total contratada por el cliente.",
  },
  margin: {
    source: "brief",
    description:
      "Margen aplicado sobre el coste de plataforma para llegar al precio cliente.",
  },
  marginIdeal: {
    source: "calc",
    inputs: "mixed",
    description:
      "Margen que pide el coste real hasta ayer: 1 − Inv. Plat. Nec. (Total) / Inversión Cliente. Se puede editar; al aceptar en Inv. Plat. Nec. (Total) se guarda desde mañana (desde hoy si es el último día) hasta el fin de la campaña.",
  },
  platformInversion: {
    source: "brief",
    description:
      "Inversión plataforma del briefing: la que quedaba para gastar en plataforma con el margen inicial. No cambia al aceptar un margen nuevo.",
  },
  currentPlatformInversion: {
    source: "calc",
    inputs: "mixed",
    description:
      "Inversión plataforma con el margen guardado hoy: inversión cliente × (1 − margen). Al aceptar un margen nuevo pasa a la que deja ese margen; sin cambios de margen coincide con la inicial. Es el techo de gasto real y la base de Inv. Prevista Hasta Ayer, Gasto / Inversión Plataforma Actual y Pacing Inv. %.",
  },
  targetVolume: {
    source: "brief",
    description:
      "Volumen objetivo del plan, en las unidades que marca el objetivo (impresiones, clicks, visualizaciones o conversiones).",
  },

  // ── KPIs Generales ──────────────────────────────────────────────────────────
  impressions: {
    source: "api",
    description: "Impresiones acumuladas que reporta la plataforma.",
  },
  clicks: {
    source: "api",
    description: "Clicks acumulados que reporta la plataforma.",
  },
  completeViews: {
    source: "api",
    description:
      "Visualizaciones completas acumuladas (vídeo visto al 100%) que reporta la plataforma.",
  },
  conversions: {
    source: "api",
    description: "Conversiones acumuladas que reporta la plataforma.",
  },

  // ── Alerta ──────────────────────────────────────────────────────────────────
  status: {
    source: "calc",
    inputs: "mixed",
    description:
      "Semáforo del WIP. Sin fechas (falta plan) · Pendiente de inicio (aún no arranca) · Inicio pendiente (ya debería entregar y sigue a cero) · Atrasada (vol. obtenido hasta ayer <90% del esperado) · En ritmo (90–110%) · Adelantada (>110%) · Finalizada (pasó la fecha de fin).",
  },
  projection: {
    source: "calc",
    inputs: "mixed",
    description:
      "Con cuánto volumen acabaría la línea frente al previsto si sigue a este ritmo, igual que la predicción de las gráficas: lo obtenido hasta ayer más, cada día que queda, lo mismo que entregó ayer. Correcta (±10%) · Se pasa (>+10%) · No llega (<−10%) · Datos a 0 (ayer no llegó nada: parada o sin sincronizar). La flecha va recta si es correcta, sube si se pasa y baja si no llega.",
  },
  expectedVolumePercent: {
    source: "calc",
    inputs: "brief",
    description:
      "Volumen esperado hasta ayer / volumen previsto. Cuánto del plan tocaba llevar entregado.",
  },
  expectedVolumeUntilYesterday: {
    source: "calc",
    inputs: "brief",
    description:
      "Volumen que debería llevarse a cierre de ayer si la entrega fuese lineal a lo largo de la campaña.",
  },
  obtainedVolumePercent: {
    source: "calc",
    inputs: "mixed",
    description:
      "Volumen obtenido hasta ayer / volumen previsto. Cuánto del plan se lleva entregado de verdad.",
  },
  accumulatedVolumeUntilYesterday: {
    source: "calc",
    inputs: "api",
    description:
      "Volumen real acumulado a cierre de ayer. Excluye el día en curso a propósito, porque la API lo reporta incompleto.",
  },
  impressionsUntilYesterday: {
    source: "api",
    description:
      "Impresiones reales conseguidas a cierre de ayer, tal cual las reporta la API de la plataforma. Siempre son impresiones, aunque el objetivo de la línea sea CPC, CPV o CPA.",
  },
  volumeProgress: {
    source: "calc",
    inputs: "mixed",
    description:
      "Volumen obtenido hasta ayer / volumen previsto. La barra es lo obtenido y la marca vertical, lo que tocaba llevar a cierre de ayer. Verde si lleva ≥90% de lo esperado, ámbar 70–90%, rojo por debajo. En el CSV sale el % obtenido.",
  },
  volumeVariation: {
    source: "calc",
    inputs: "mixed",
    description:
      "Obtenido menos esperado hasta ayer, en unidades. Positivo = por delante del plan.",
  },

  // ── Pacing Inversión ────────────────────────────────────────────────────────
  expectedPlatformInvestmentUntilYesterday: {
    source: "calc",
    inputs: "mixed",
    description:
      "Inversión de plataforma actual (la del margen guardado hoy) que debería llevarse gastada a cierre de ayer si el gasto fuese lineal a lo largo de la campaña: inversión plataforma actual × días transcurridos / días totales.",
  },
  realPlatformInvestmentUntilYesterday: {
    source: "api",
    description:
      "Inversión real de plataforma a cierre de ayer: el gasto que reporta la API (DV360, Meta…), sin margen. En verde si va a ±10% de la prevista hasta ayer, ámbar a ±25%, rojo por encima.",
  },
  platformBudgetProgress: {
    source: "calc",
    inputs: "mixed",
    description:
      "Gastado en plataforma hasta ayer / inversión plataforma actual (la del margen guardado hoy). La barra es lo gastado y la marca vertical, lo que tocaba llevar gastado a cierre de ayer sobre esa inversión. Verde si va a ±10% de lo previsto, ámbar a ±25%, rojo por encima. En el CSV sale el % gastado.",
  },
  totalPlatformCost: {
    source: "api",
    description:
      "Coste real acumulado en la plataforma: el gasto que reporta la API.",
  },
  pacingInvestmentPercent: {
    source: "calc",
    inputs: "mixed",
    description:
      "Coste real de plataforma / inversión plataforma actual (la del margen guardado hoy). Por encima de 100% es sobregasto.",
  },
  yesterdayPacingInvestmentVar: {
    source: "calc",
    inputs: "mixed",
    description:
      "Desvío del gasto de ayer: (coste cliente de ayer − coste cliente estimado de ayer) / estimado.",
  },
  investmentNeededTotalReal: {
    source: "calc",
    inputs: "mixed",
    description:
      "Inversión de plataforma que haría falta para todo el volumen previsto, al coste unitario real hasta ayer. Vacío mientras no haya coste real. Con «Aceptar» se guarda el margen ideal desde mañana (desde hoy si es el último día) y, en las líneas de DV360 a nivel IO, esta inversión pasa al presupuesto de la IO. Si la línea ya llegó al objetivo, esa inversión es lo gastado hasta ayer: sirve para cerrarla bajando el presupuesto.",
  },
  investmentNeededRemainingReal: {
    source: "calc",
    inputs: "mixed",
    description:
      "Igual que la anterior pero solo para el volumen que queda por entregar. Compárala con la inversión de plataforma restante para ver si el dinero da.",
  },
  realUnitCostPlatform: {
    source: "calc",
    inputs: "api",
    description:
      "Coste unitario real en plataforma según la API: coste real hasta ayer / volumen hasta ayer (×1.000 si el objetivo es CPM o CPMV).",
  },
  realUnitCostClient: {
    source: "calc",
    inputs: "mixed",
    description:
      "El coste unitario real llevado a precio cliente, aplicándole el margen del brief.",
  },

  // ── Otros ───────────────────────────────────────────────────────────────────
  briefingId: {
    source: "brief",
    description: "ID interno del briefing. Útil para soporte y trazabilidad.",
  },
  concatenado: {
    source: "calc",
    inputs: "brief",
    description:
      "Clave cliente_plataforma_tipo_mes_año que usa el Excel del WIP para cruzar filas.",
  },
  briefingType: {
    source: "brief",
    description: "Tipo de briefing (buy media, paid media gestionado, etc.).",
  },
  campaignFormat: {
    source: "brief",
    description: "Formato creativo declarado en el briefing.",
  },
  currency: {
    source: "brief",
    description: "Moneda del plan.",
  },
  adserverCost: {
    source: "brief",
    description: "Coste añadido de ad serving, a nivel CPM.",
  },
  zeotapDataCost: {
    source: "brief",
    description: "Coste añadido de datos de proveedor (Zeotap), a nivel CPM.",
  },
  impressionTrackerCost: {
    source: "brief",
    description: "Coste añadido del tracker de impresiones, a nivel CPM.",
  },
  clickTrackerCost: {
    source: "brief",
    description: "Coste añadido del tracker de clicks, a nivel CPM.",
  },
  adtechCost: {
    source: "brief",
    description: "Coste añadido de ad tech, a nivel CPM.",
  },
  addedCostsCpm: {
    source: "calc",
    inputs: "brief",
    description: "Suma de ad serving + data + trackers + ad tech, a nivel CPM.",
  },
  addedCostsTotal: {
    source: "calc",
    inputs: "brief",
    description:
      "Los costes añadidos convertidos a euros absolutos según el volumen y el objetivo.",
  },
  totalDays: {
    source: "calc",
    inputs: "brief",
    description: "Días que dura la subcampaña según las fechas del brief.",
  },
  elapsedDays: {
    source: "calc",
    inputs: "brief",
    description:
      "Días transcurridos hasta el cierre de ayer (el día en curso no cuenta).",
  },
  remainingDays: {
    source: "calc",
    inputs: "brief",
    description: "Días que quedan hasta la fecha de fin.",
  },
  timePercent: {
    source: "calc",
    inputs: "brief",
    description:
      "Porcentaje de campaña consumido en tiempo. Es la referencia contra la que se juzga el % de volumen.",
  },
  accumulatedVolume: {
    source: "calc",
    inputs: "api",
    description:
      "Volumen real acumulado según el objetivo (impresiones, clicks, visualizaciones o conversiones), incluyendo el día en curso.",
  },
  remainingVolume: {
    source: "calc",
    inputs: "mixed",
    description: "Volumen previsto que queda por entregar.",
  },
  dailyVolumeNeeded: {
    source: "calc",
    inputs: "mixed",
    description:
      "Volumen restante / días restantes: a qué ritmo diario hay que ir para cerrar el objetivo.",
  },
  volumePercent: {
    source: "calc",
    inputs: "mixed",
    description: "Volumen acumulado / volumen previsto.",
  },
  volumeVariationPercent: {
    source: "calc",
    inputs: "mixed",
    description:
      "La variación de volumen en porcentaje sobre el esperado hasta ayer.",
  },
  accumulatedInvestment: {
    source: "calc",
    inputs: "api",
    description:
      "Inversión consumida. Ojo: el backend la iguala al coste real de plataforma, no la recalcula a precio cliente.",
  },
  remainingInvestment: {
    source: "calc",
    inputs: "mixed",
    description:
      "Inversión cliente contratada menos el coste real de plataforma ya gastado.",
  },
  remainingPlatformInvestment: {
    source: "calc",
    inputs: "mixed",
    description:
      "Inversión de plataforma prevista menos coste real: el dinero que queda para gastar en plataforma.",
  },
  investmentNeededTotal: {
    source: "calc",
    inputs: "brief",
    description:
      "Inversión de plataforma necesaria para el volumen previsto usando el coste unitario planificado (no el real). Es la versión «según plan».",
  },
  investmentNeededRemaining: {
    source: "calc",
    inputs: "mixed",
    description:
      "Inversión de plataforma necesaria para el volumen restante usando el coste unitario planificado.",
  },
  pacingPercent: {
    source: "calc",
    inputs: "mixed",
    description:
      "Vol. obtenido hasta ayer / vol. esperado hasta ayer. Es el número que decide el estado: <90% atrasada, >110% adelantada.",
  },
  yesterdayImpressions: {
    source: "api",
    description: "Impresiones del día de ayer que reporta la plataforma.",
  },
  yesterdayClicks: {
    source: "api",
    description: "Clicks del día de ayer que reporta la plataforma.",
  },
  yesterdayCompleteViews: {
    source: "api",
    description:
      "Visualizaciones completas del día de ayer que reporta la plataforma.",
  },
  yesterdayConversions: {
    source: "api",
    description: "Conversiones del día de ayer que reporta la plataforma.",
  },
  yesterdayRealCost: {
    source: "api",
    description: "Coste real de ayer en plataforma, según la API.",
  },
  dailyTargetVolume: {
    source: "calc",
    inputs: "brief",
    description:
      "Objetivo diario lineal: volumen previsto / días totales. Sirve para medir el día de ayer; no decide el estado.",
  },
  yesterdayActualVolume: {
    source: "calc",
    inputs: "api",
    description:
      "Volumen conseguido ayer, en las unidades que marca el objetivo.",
  },
  yesterdayVolumeDiff: {
    source: "calc",
    inputs: "mixed",
    description: "Volumen de ayer menos objetivo diario, en unidades.",
  },
  yesterdayVolumeVar: {
    source: "calc",
    inputs: "mixed",
    description:
      "La diferencia de volumen de ayer en porcentaje del objetivo diario. Informativo: el estado lo decide el acumulado (Pacing %).",
  },
  yesterdayClientCost: {
    source: "calc",
    inputs: "mixed",
    description:
      "Coste real de ayer llevado a precio cliente (con el margen del brief aplicado).",
  },
  yesterdayEstimatedClientCost: {
    source: "calc",
    inputs: "brief",
    description:
      "Lo que debería haber costado ayer a precio cliente: objetivo diario × coste unitario cliente.",
  },
  viewThroughRate: {
    source: "calc",
    inputs: "api",
    description: "VTR: visualizaciones completas / impresiones.",
  },
  clickThroughRate: {
    source: "calc",
    inputs: "api",
    description: "CTR: clicks / impresiones.",
  },
  conversionRate: {
    source: "calc",
    inputs: "api",
    description: "Conversiones / clicks.",
  },
  realPlatform: {
    source: "brief",
    description:
      "Proveedor donde se compra de verdad la línea (DV360, Outbrain, Taboola, Meta, Spotify…): el de la campaña interna enlazada. «Compra directa» si no hay conexión. Solo la ven los administradores.",
  },
  dataSource: {
    source: "brief",
    description:
      "De dónde salen las métricas: API de la plataforma, o valores manuales del partner en una compra directa.",
  },
  engagements: {
    source: "api",
    description:
      "Interacciones acumuladas hasta ayer (Meta, TikTok, Google Ads…). 0 en plataformas que no las dan.",
  },
  engagementRate: {
    source: "calc",
    inputs: "api",
    description:
      "Interacciones / impresiones. Vacío si la plataforma no da interacciones.",
  },
  uniqueUsers: {
    source: "api",
    description:
      "Usuarios únicos del periodo. Solo cuando la cifra es exacta: la campaña entera, un ad set de Meta / ad group de TikTok, o el dato manual de una compra directa. El reach no se suma, así que no tiene total.",
  },
  frequency: {
    source: "calc",
    inputs: "api",
    description: "Impresiones / usuarios únicos. Vacío sin alcance.",
  },
  measurableImpressions: {
    source: "api",
    description: "Impresiones medibles (Active View) acumuladas hasta ayer.",
  },
  viewableImpressions: {
    source: "api",
    description: "Impresiones visibles (Active View) acumuladas hasta ayer.",
  },
  viewabilityRate: {
    source: "calc",
    inputs: "api",
    description: "Impresiones visibles / impresiones medibles.",
  },
  firstQuartileViews: {
    source: "api",
    description: "Vídeos vistos hasta el 25 %.",
  },
  midpointViews: {
    source: "api",
    description: "Vídeos vistos hasta el 50 %.",
  },
  thirdQuartileViews: {
    source: "api",
    description: "Vídeos vistos hasta el 75 %.",
  },
  yesterdayEngagements: {
    source: "api",
    description: "Interacciones del día de ayer.",
  },
  accumulatedClientCost: {
    source: "calc",
    inputs: "api",
    description:
      "Coste de cliente acumulado hasta ayer: con el margen diario aplicado, o el de plataforma si la línea no lleva margen (paid media que paga el cliente).",
  },
  cpmReal: {
    source: "calc",
    inputs: "api",
    description:
      "Coste cliente acumulado × 1000 / impresiones, sea cual sea el objetivo.",
  },
  cpcReal: {
    source: "calc",
    inputs: "api",
    description: "Coste cliente acumulado / clicks.",
  },
  cpvReal: {
    source: "calc",
    inputs: "api",
    description: "Coste cliente acumulado / visualizaciones completas.",
  },
  cpeReal: {
    source: "calc",
    inputs: "api",
    description: "Coste cliente acumulado / interacciones.",
  },
  cpaReal: {
    source: "calc",
    inputs: "api",
    description: "Coste cliente acumulado / conversiones.",
  },
};

const FALLBACK_META: ColumnMeta = { source: "calc", description: "" };

/** Meta for a column id. Never throws: an undocumented column degrades to
 *  "calculado" with no description instead of breaking the header. */
export function getColumnMeta(id: string): ColumnMeta {
  return COLUMN_META[id] ?? FALLBACK_META;
}

/**
 * Small provenance marker. In the table header only "api" columns get one —
 * badging every column would turn the header into noise — while the info
 * dialog and the column menu show all three.
 */
export const SourceBadge: React.FC<{
  source: ColumnSource;
  title?: string;
}> = ({ source, title }) => {
  const meta = SOURCE_META[source];
  return (
    <Box
      component="span"
      title={title}
      sx={{
        flexShrink: 0,
        display: "inline-block",
        px: 0.4,
        borderRadius: 0.5,
        fontSize: 8.5,
        lineHeight: 1.5,
        fontWeight: 700,
        letterSpacing: 0.3,
        color: "#fff",
        backgroundColor: meta.color,
        textTransform: "uppercase",
      }}
    >
      {meta.short}
    </Box>
  );
};

/** Rich tooltip body for a column header: name, provenance and what it means. */
export const ColumnTooltip: React.FC<{
  label: string;
  source: ColumnSource;
  description: string;
  inputs?: CalcInput;
}> = ({ label, source, description, inputs }) => (
  <Box sx={{ py: 0.5 }}>
    <Box sx={{ fontWeight: 700, fontSize: 12, mb: 0.25 }}>{label}</Box>
    <Box sx={{ fontSize: 10.5, opacity: 0.75, mb: description ? 0.5 : 0 }}>
      Origen: {SOURCE_META[source].label}
      {source === "calc" && inputs ? ` · ${CALC_INPUT_META[inputs].label}` : ""}
    </Box>
    {description ? (
      <Box sx={{ fontSize: 11.5, lineHeight: 1.45 }}>{description}</Box>
    ) : null}
  </Box>
);
